import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, ArrowRight } from 'lucide-react'
import { useSearchChurches } from '../hooks/useChurches'
import { useDebounce } from '../hooks/useDebounce'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'

function denominationLabel(key: string | null) {
  if (!key) return null
  return DENOMINATION_OPTIONS.find((d) => d.key === key)?.label ?? null
}

const MIN_QUERY_LENGTH = 3
const MAX_RESULTS = 5

interface Props {
  className?: string
}

/**
 * Inline "type your church, hit lookup" entry point — the fast-track into
 * claiming, replacing the browse-the-map detour for cold-invite visitors.
 */
export function ChurchQuickFind({ className }: Props) {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)
  const trimmed = debouncedQuery.trim()
  const searchEnabled = trimmed.length >= MIN_QUERY_LENGTH

  const { data, isLoading, isFetching } = useSearchChurches({
    q: trimmed,
    limit: MAX_RESULTS,
    enabled: searchEnabled,
  })
  const results = data?.data ?? []
  const showPanel = trimmed.length > 0
  const showNoMatch = searchEnabled && !isLoading && !isFetching && results.length === 0

  return (
    <div className={className}>
      <div className="relative">
        <Search
          size={17}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter your church's name or address"
          aria-label="Find your church"
          className="w-full h-12 pl-11 pr-4 rounded-xl border border-line bg-surface text-[15px] text-ink"
        />
      </div>

      {showPanel && (
        <div className="mt-2 rounded-xl border border-line-soft bg-surface overflow-hidden">
          {searchEnabled && (isLoading || isFetching) && (
            <div className="px-4 py-3 text-[13px] text-muted">Searching…</div>
          )}

          {!isLoading && !isFetching && results.length > 0 && (
            <ul className="m-0 p-0 list-none divide-y divide-line-soft">
              {results.map((church) => (
                <li key={church.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-ink truncate">{church.name}</div>
                    <div className="text-[12.5px] text-muted truncate">
                      {[church.city && church.state ? `${church.city}, ${church.state}` : church.city || church.state, denominationLabel(church.denomination)]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {church.claimed ? (
                    <Link
                      to={`/churches/${church.slug}`}
                      className="shrink-0 text-[12.5px] font-medium text-muted no-underline whitespace-nowrap"
                    >
                      Already on Fellowship
                    </Link>
                  ) : (
                    <Link
                      to={`/churches/${church.id}/claim`}
                      className="shrink-0 inline-flex items-center gap-1 h-9 px-4 rounded-lg bg-primary text-on-primary text-[13px] font-semibold no-underline whitespace-nowrap"
                    >
                      Claim <ArrowRight size={13} />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}

          {showNoMatch && (
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <span className="text-[13px] text-ink-2">Can&apos;t find your church?</span>
              <Link
                to="/churches/new"
                className="text-[13px] font-semibold text-primary no-underline whitespace-nowrap"
              >
                Add your church →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
